import React from 'react';
import { Button, Tag, Tooltip, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';

import SharedFeeCell, { FeeRateCell } from '../../../components/FeeCell.jsx';
import { requestType } from '../../../domain/requestType.js';
import StatusTag from '../../../components/StatusTag.jsx';
import { formatInstant, formatMoney, formatUnits } from '../../../format';
import { STATUS_KIND, statusHelp } from '../../../status/statusHelp.js';
import { HOLD_REASON_LABEL, ORDER_TYPE_COLOUR, ORDER_TYPE_LABEL, STATE_COLOUR, STATE_LABEL } from './orders.js';
import { PRICE_CHANGED_TEXT, figureOf, pricingDisplay, reviewBy, reviewerNames, reviewsSummary, rolledTooltip } from './orderRows.js';

const { Text } = Typography;

const dash = () => <Text type="secondary">—</Text>;

/** The colour of each price basis's tag: provisional, the dealing date's own, an estimate. */
const TAG_COLOUR = Object.freeze({ 'prov.': 'default', 'dealing-date price': 'green', estimate: 'purple' });

/** A price basis tag (design 8.2), with what it means on hover. */
export const BasisTag = ({ tag }) => (tag ? (
    <Tooltip title={tag.tooltip}>
        <Tag className="price-basis-tag" color={TAG_COLOUR[tag.label]}>{tag.label}</Tag>
    </Tooltip>
) : null);

/**
 * A transaction's state, with why it is held or rejected underneath. The tag
 * explains itself on hover or focus (status/statusHelp.js): what the state
 * means and what happens next. A held transaction's tooltip names its own
 * reason and what to do about it, which says more than "On Hold" in general.
 */
const stateHelp = (order) => {
    const held = order.state === 'HELD' && statusHelp(STATUS_KIND.HOLD_REASON, order.holdReasonCode);
    return held ? 'On hold: ' + held : statusHelp(STATUS_KIND.TRANSACTION, order.state);
};

export const StateTag = ({ order }) => (
    <>
        <StatusTag kind={STATUS_KIND.TRANSACTION} code={order.state} color={STATE_COLOUR[order.state]}
            className="order-state-tag" label={STATE_LABEL[order.state]} help={stateHelp(order)} />
        {order.state === 'HELD' && order.holdReasonCode && (
            <div>
                <Text type="secondary" style={{ fontSize: 11.5 }}>{HOLD_REASON_LABEL[order.holdReasonCode] ?? order.holdReasonCode}</Text>
            </div>
        )}
        {order.state === 'REJECTED' && order.rejectReason && (
            <div>
                <Text type="secondary" style={{ fontSize: 11.5 }}>{order.rejectReason}</Text>
            </div>
        )}
    </>
);

/**
 * An order's fee, read-only: the fee the order holds - repricing's provisional
 * figure until allocation charges the rate - and the % beside it, a
 * subscription's entry rate (the review's own when set) or a redemption's exit
 * rate (components/FeeCell.jsx, design 2026-10-15 section 5). Nobody types a
 * fee in here: a review changes a subscription's rate (IP-139).
 */
export const FeeCell = ({ order, showRate = true }) => <SharedFeeCell row={order} showRate={showRate} />;

/** A Units or Amount cell: the figure, and its price basis when it is the side the price moves (figureOf). */
/**
 * In place of a figure LIV worked out at the T-1 price, until the Core reprices
 * (orderRows.js figureOf's hidden block): says when it will be known, not a dash.
 */
export const AwaitingRepricing = () => (
    <Tooltip title="Worked out when the Core prices this transaction: the figure LIV sent is at the previous day's price">
        <Text type="secondary" className="order-awaiting-repricing">After pricing</Text>
    </Tooltip>
);

/** The Request type cell: the side the investor fixed (domain/requestType.js). */
export const RequestTypeCell = ({ row }) => {
    const type = requestType(row);
    return type ? <span className="order-request-type">{type}</span> : dash();
};

const FigureCell = ({ order, side, format }) => {
    const { value, tag, awaitingRepricing } = figureOf(order, side);
    if (awaitingRepricing) return <AwaitingRepricing />;
    if (value === null || value === undefined) return dash();
    return (
        <span className="order-figure-cell">
            {format(value)}
            <BasisTag tag={tag} />
        </span>
    );
};

/** The Unit price cell (B-3; design 4.5): the provisional price and its basis, or why there is none. */
export const PriceCell = ({ order }) => {
    const shown = pricingDisplay(order);
    if (shown.price !== null) {
        return (
            <span className="order-figure-cell">
                {formatUnits(shown.price)}
                <BasisTag tag={shown.tag} />
            </span>
        );
    }
    return (
        <span className="order-figure-cell">
            <Tooltip title={shown.tooltip}><Text type="secondary">{shown.text}</Text></Tooltip>
            {shown.tag && shown.text !== '—' && <BasisTag tag={shown.tag} />}
            {shown.note && <Text type="secondary" className="order-price-note">{shown.note}</Text>}
        </span>
    );
};

/** The Reviews cell (design 8.2): the cycle's count, "incl. you", the reviewers named on hover. */
export const ReviewsCell = ({ order }) => {
    const summary = reviewsSummary(order);
    return (
        <>
            <Tooltip title={summary.tooltip}>
                <span className={'order-reviews' + (summary.count ? '' : ' is-none')}>{summary.text}</span>
            </Tooltip>
            {/* IP-197: reviewed before its latest pricing - still approvable, but flagged so the checker looks at the new figures. */}
            {order?.repricedSinceReview === true && (
                <Tooltip title="Priced again after its last review: check the new figures in its history before approving">
                    <Tag color="gold" className="price-changed-tag">{PRICE_CHANGED_TEXT}</Tag>
                </Tooltip>
            )}
        </>
    );
};

/** The Review by cell (design 8.2; IP-125, IP-126): the cut-off to review by, red when it is close, and the rolls so far. */
export const ReviewByCell = ({ order, nowMs }) => {
    const { deadline, rolled, rolledText } = reviewBy(order, nowMs);
    if (!deadline && !rolled) return null;
    return (
        <span className="review-by-cell">
            {deadline && (
                <Tooltip title={deadline.tooltip}>
                    <Tag className="review-by-tag" color={deadline.urgent ? 'red' : 'gold'}>{deadline.text}</Tag>
                </Tooltip>
            )}
            {rolledText && (
                <Tooltip title={rolledTooltip(rolled)}>
                    <Tag className="review-by-rolled">{rolledText}</Tag>
                </Tooltip>
            )}
        </span>
    );
};

/**
 * The columns every order table on the tab shares, for GET /orders rows
 * (orders/reads.js mapOrder). The pending view, and the approval queue, add
 * their own after these.
 *
 * @param {{ onOpen?: (order) => void, blocked?: Map<string, string[]>, omit?: string[] }} options
 *   onOpen opens an order's detail; blocked marks the dealing dates the pricing guard holds; omit
 *   drops columns by key (a queue of READY orders has no use for their state)
 */
export const orderColumns = ({ onOpen, blocked, omit = [] } = {}) =>
    allColumns({ onOpen, blocked }).filter((c) => !omit.includes(c.key));

/** What the approval queue leaves out: every order in it is READY, and who reviewed it matters more than its receipt. */
export const QUEUE_OMITS = Object.freeze(['state', 'receivedAt']);

/** The Reviews column (design 8.2, 8.5). */
export const reviewsColumn = () => ({ title: 'Reviews', key: 'reviews', width: 100, render: (_, order) => <ReviewsCell order={order} /> });

/** The Review by column (design 8.2): nowMs is the clock the cut-offs are read against. */
export const reviewByColumn = (nowMs) => ({ title: 'Review by', key: 'reviewBy', width: 150, render: (_, order) => <ReviewByCell order={order} nowMs={nowMs} /> });

/** The Reviewed by column (design 8.5): every reviewer of the order, named. */
export const reviewedByColumn = () => ({
    title: 'Reviewed by',
    key: 'reviewedBy',
    width: 200,
    render: (_, order) => (reviewerNames(order).length ? reviewerNames(order).join(', ') : dash())
});


const allColumns = ({ onOpen, blocked }) => [
    {
        title: 'Transaction ID',
        dataIndex: 'transactionRef',
        key: 'transactionRef',
        width: 240,
        render: (ref, order) => (onOpen
            ? <Button type="link" size="small" className="txn-id txn-id-link" onClick={() => onOpen(order)}>{ref}</Button>
            : <span className="txn-id">{ref}</span>)
    },
    {
        title: 'Type',
        dataIndex: 'orderType',
        key: 'orderType',
        width: 195,
        render: (type, order) => (
            <Tag className="order-type-tag" color={ORDER_TYPE_COLOUR[type]}>
                {ORDER_TYPE_LABEL[type] ?? type}{order.isFullHolding ? ' (full holding)' : ''}
            </Tag>
        )
    },
    // LKR based or unit based: which side the investor fixed, and so which figure the price moves.
    { title: 'Request type', key: 'requestType', width: 130, render: (_, order) => <RequestTypeCell row={order} /> },
    { title: 'Fund', dataIndex: 'fundId', key: 'fundId', width: 80 },
    { title: 'Holder', dataIndex: 'holderNo', key: 'holderNo', width: 110, render: (v) => v ?? dash() },
    // IP-202 (owner, 19 Oct 2026): the strategy activation code beside every transaction.
    { title: 'Strategy code', dataIndex: 'strategyActivationCode', key: 'strategyActivationCode', width: 130, render: (v) => v ?? dash() },
    { title: 'Amount (LKR)', dataIndex: 'amount', key: 'amount', align: 'right', width: 150, render: (_, order) => <FigureCell order={order} side="amount" format={formatMoney} /> },
    { title: 'Units', dataIndex: 'units', key: 'units', align: 'right', width: 140, render: (_, order) => <FigureCell order={order} side="units" format={formatUnits} /> },
    { title: 'Unit price', key: 'unitPrice', align: 'right', width: 170, render: (_, order) => <PriceCell order={order} /> },
    // Every transaction's fee, and its % in a column of its own: a subscription's entry fee (the review's own rate
    // marked), a redemption's exit fee on its young units (components/FeeCell.jsx).
    { title: 'Fee (LKR)', key: 'fee', align: 'right', width: 130, render: (_, order) => <FeeCell order={order} showRate={false} /> },
    { title: 'Fee %', key: 'feeRate', width: 200, render: (_, order) => <FeeRateCell row={order} /> },
    { title: 'State', dataIndex: 'state', key: 'state', width: 170, render: (_, order) => <StateTag order={order} /> },
    {
        title: 'Effective date',
        dataIndex: 'dealingDate',
        key: 'dealingDate',
        width: 200,
        render: (date, order) => {
            // A parked order has no dealing date until LIV completes it (IP-83).
            if (!date) return <Text type="secondary">{order.state === 'AWAITING_LIV' ? 'When LIV completes' : '—'}</Text>;
            const missing = blocked?.get(date);
            return (
                <span className="dealing-date-cell">
                    {date}
                    {order.dealingDateBasis === 'PROVISIONAL' && (
                        <Tooltip title="Provisional: set again when the money clears">
                            <Tag className="dealing-date-prov">prov.</Tag>
                        </Tooltip>
                    )}
                    {missing && (
                        <Tooltip title={'No published price for ' + missing.join(', ') + ' on ' + date + ': approval is held'}>
                            <WarningOutlined className="dealing-date-warn" />
                        </Tooltip>
                    )}
                </span>
            );
        }
    },
    { title: 'Received', dataIndex: 'receivedAt', key: 'receivedAt', width: 170, render: formatInstant }
];
